/**
 * Schedule entity model class
 *
 * This file is part of Minasa
 * (c) Kaden und Partner AG
 */
import BaseModel from './BaseModel';
import BaseCollection from './BaseCollection';
import dayjs from 'dayjs';

export class Schedule extends BaseModel {
    static RECURRING_TYPE_SINGLE = 'single';
    static RECURRING_TYPE_DAILY = 'daily';
    static RECURRING_TYPE_WEEKLY = 'weekly';
    static RECURRING_TYPE_MONTHLY = 'monthly';
    static RECURRING_TYPE_YEARLY = 'yearly';

    defaults() {
        return {
            // entity properties
            id: null,
            uuid: null,
            event_id: null,
            location_id: null,
            start_date: null,
            end_date: null,
            event_time_start: null,
            event_time_end: null,
            opening_time_start: null,
            opening_time_end: null,
            exceptions: null,
            target_audience_id: null,
            typical_age_range: null,
            presales: null,
            presales_link: null,
            ticketprices: null,
            description: null,
            accessibility: null,
            recurring_type: null,
            recur_every_n: null,
            recurrence_config: { weekdays: [] },
            created: null,
            modified: null,
            last_generated: null,
            location_title: null,
            location_zip: null,
            location_city: null,
            bp_import_id: null,
            bp_import_date: null,
            bp_import_origin: null,
            is_foreign_import: null,
            schedule_status_id: null,
            schedule_status_ident: null,
            schedule_status_value: null,
            tags: [],
            is_sold_out: false,
            print_title: null,
            online_event_url: null,

            // view properties
            schedule_location_title: null,
            target_audience: null,
        };
    }

    get isSingle() {
        return this.recurring_type === Schedule.RECURRING_TYPE_SINGLE;
    }

    get isRecurring() {
        return (
            [
                Schedule.RECURRING_TYPE_DAILY,
                Schedule.RECURRING_TYPE_WEEKLY,
                Schedule.RECURRING_TYPE_MONTHLY,
                Schedule.RECURRING_TYPE_YEARLY,
            ].indexOf(this.recurring_type) !== -1 || this.recur_every_n > 0
        );
    }

    get dateTimeString() {
        const startDateString = this.dateString(this.start_date);
        const endDateString = this.dateString(this.end_date);

        const sameDay =
            !startDateString || !endDateString || dayjs(startDateString).isSame(dayjs(endDateString), 'day');

        let dateString = '';
        if (startDateString) {
            dateString += startDateString;
        }
        if (endDateString && !sameDay) {
            dateString += ` - ${endDateString}`;
        }

        const startTimeString = this.timeString(this.event_time_start);
        const endTimeString = this.timeString(this.event_time_end);
        let timeString = '';
        if (startTimeString) {
            timeString += startTimeString;
        }
        if (endTimeString) {
            timeString += ` - ${endTimeString}`;
        }

        let dateTimeString = '';
        if (dateString) {
            dateTimeString += dateString;
        }
        if (timeString) {
            if (dateTimeString) {
                dateTimeString += ` `;
            }
            dateTimeString += `${timeString}`;
        }

        return dateTimeString;
    }

    dateString(date) {
        if (!date) {
            return null;
        }
        if (!(date instanceof dayjs)) {
            date = dayjs(date);
        }
        if (!date?.isValid()) {
            return null;
        }
        return date?.formatIf('dd, DD.MM.YYYY');
    }

    timeString(time) {
        if (!time) {
            return null;
        }
        // regex lookbehind is not yet available in safari, so we use a capture group
        return time?.replace(/([0-9]{1,2}:[0-9]{1,2})(:[0-9]{1,2})?/, '$1');
    }

    mutations() {
        return {
            id: (id) => (Number(id) > 0 ? Number(id) : null),

            start_date: dayjs,
            end_date: dayjs,

            recurrence_config: (recurrence_config) => {
                recurrence_config = recurrence_config || {};
                recurrence_config.weekdays = recurrence_config?.weekdays || [];
                if (!(recurrence_config?.weekdays instanceof Array)) {
                    recurrence_config.weekdays = [];
                }
                // map weekdays to numbers:
                recurrence_config.weekdays = recurrence_config.weekdays.map(Number);
                return recurrence_config;
            },

            /**
             * Exceptions is an array of exception objects:
             * [{
             *  start_date: dayjs,
             *  end_date: dayjs,
             *  description: string,
             * }]
             * @returns
             */
            exceptions: (value) => {
                if (!(value instanceof Array)) {
                    return [];
                }
                return value.map((item) => {
                    return {
                        start_date: item.start_date instanceof dayjs ? item.start_date : dayjs(item.start_date),
                        end_date: item.end_date instanceof dayjs ? item.end_date : dayjs(item.end_date),
                        description: item.description || '',
                    };
                });
            },

            last_generated: dayjs,
            created: dayjs,
            modified: dayjs,
            is_sold_out: Boolean,
        };
    }

    entityName() {
        return 'Schedule';
    }
}

export class ScheduleCollection extends BaseCollection {
    options() {
        return {
            model: Schedule,
        };
    }

    entityName() {
        return 'Schedule';
    }
}
